import {DeliveryMethods, OrderableTypes} from "@hps/hops-sdk-js";

import Store from "App/Store.js";
import ShippingBehaviours from "Shop/ShippingBehaviours";

/**
 * Delivery method utility methods
 *
 * @package HOPS
 * @subpackage Checkout
 * @copyright Heritage Operations Processing Limited
 */
class DeliveryMethodUtils {

	/**
	 * Determine if a basket item requires delivery
	 * 
	 * @param {Object} basketItem 
	 * @returns 
	 */
	static doesBasketItemRequireDelivery(basketItem) {

		const shippingBehavioursWithDelivery = [
			ShippingBehaviours.IncludeDeliveryIncludePostage,
			ShippingBehaviours.IncludeDeliveryExcludePostage
		];

		if (basketItem.OrderableType === OrderableTypes.RailwayProduct) return shippingBehavioursWithDelivery.includes(basketItem.Item.ShippingBehaviour);
		else return DeliveryMethods.OrderableTypesRequiringDelivery.includes(basketItem.OrderableType);

	}


	/**
	 * Validate each item in a collection of basket items to determine 
	 * whether there's a permissible set of delivery methods for it 
	 * within the collection.
	 *
	 * @param {Array<Object>} basketItems CheckoutBasketItem-like objects
	 */
	static validateBasketSelection(basketItems) {

		const results = {};

		for (const item of basketItems) {
			results[item.Uuid] = this.validateBasketItemSelection(item, basketItems);
		}

		return results;

	}


	/**
	 * Validate whether a collection of basket items contains a 
	 * permissible delivery method item for a given basket item.
	 *
	 * @param {Object} basketItem CheckoutBasketItem-like object
	 * @param {Array<Object>} basketItems CheckoutBasketItem-like objects
	 * @return {Boolean|undefined} `undefined` when `basketItem` doesn't support delivery
	 */
	static validateBasketItemSelection(basketItem, basketItems) {

		const deliveryMethodInstances = Store.getState().checkoutBasketUpsells.DeliveryMethodInstances;

		if (this.doesBasketItemRequireDelivery(basketItem)) {

			const relatedDeliveryItem = basketItems.find(d => d.Uuid === basketItem.DeliveryMethodItemUuid);

			// Item must relate to a delivery item in the basket
			if (!relatedDeliveryItem) return false;

			// Validate a method of delivery is valid for the item's orderable type

			let orderableTypeDeliveryMethodInstanceIds = [];

			// Travel Tickets
			if (basketItem.OrderableType === OrderableTypes.TicketTravel) orderableTypeDeliveryMethodInstanceIds = deliveryMethodInstances.filter(x => x.Orderables?.Travel?.Enabled === true).map(({Id}) => Id);

			// Session Tickets
			if (basketItem.OrderableType === OrderableTypes.TicketSession) orderableTypeDeliveryMethodInstanceIds = deliveryMethodInstances.filter(x => basketItem.Item?.Session?.Product?.DeliveryMethods.map(dm => dm.Id).includes(x.Id)).map(({Id}) => Id);

			// Vouchers
			if (basketItem.OrderableType === OrderableTypes.VoucherSale) orderableTypeDeliveryMethodInstanceIds = deliveryMethodInstances.filter(x => x.Orderables?.Vouchers?.Enabled === true).map(({Id}) => Id);

			// Gift Cards
			if (basketItem.OrderableType === OrderableTypes.GiftCardInventory) orderableTypeDeliveryMethodInstanceIds = deliveryMethodInstances.filter(x => x.Orderables?.GiftCards?.Enabled === true).map(({Id}) => Id);

			// Railway Products
			if (basketItem.OrderableType === OrderableTypes.RailwayProduct) orderableTypeDeliveryMethodInstanceIds = deliveryMethodInstances.filter(x => x.Orderables?.Products?.Enabled === true).map(({Id}) => Id);

			// Selected method must exist
			return orderableTypeDeliveryMethodInstanceIds.some(deliveryMethodInstance => deliveryMethodInstance === relatedDeliveryItem.Item.Id);

		}
		else {
			return undefined;
		}
	}

}

export default DeliveryMethodUtils;
